import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldSkipPeriodicRefreshOnPath } from '../utils/platformAskAiSession';

/** How often to perform a hard refresh when the user navigates (ms). */
const REFRESH_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Performs a periodic full-page reload when the user navigates between routes,
 * so the app (PWA or browser) stays up to date without manual refresh.
 *
 * - Runs on route change only; does not reload on every navigation.
 * - Skips the initial load; first possible reload is after REFRESH_INTERVAL_MS.
 * - Works the same in PWA and regular browser.
 */
const REFRESH_PARAM = '_refresh';

export function usePeriodicRefreshOnNavigate(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionStartRef = useRef<number>(Date.now());
  const isFirstLocationRef = useRef(true);

  // Remove _refresh from URL after a cache-busting reload so the address bar stays clean
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has(REFRESH_PARAM)) {
      params.delete(REFRESH_PARAM);
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (isFirstLocationRef.current) {
      isFirstLocationRef.current = false;
      return;
    }

    if (shouldSkipPeriodicRefreshOnPath(location.pathname)) return;

    const elapsed = Date.now() - sessionStartRef.current;
    if (elapsed >= REFRESH_INTERVAL_MS) {
      // Cache-busting URL so the browser fetches fresh document/assets (hard refresh).
      // reload() alone can use cache; reload(true) is deprecated and ignored.
      const url = new URL(window.location.href);
      url.searchParams.set(REFRESH_PARAM, String(Date.now()));
      window.location.replace(url.toString());
    }
  }, [location.key, location.pathname]);
}
